import { useOrganization } from '@clerk/clerk-react';
import { ROLES, ROLE_HIERARCHY } from '../constants/roles';

/**
 * Custom hook for role-based permissions and authorization
 * Defaults to student role if no role is assigned
 * @returns {Object} Permission checking utilities
 */
export const usePermissions = () => {
  const { membership } = useOrganization();

  // Extract role and strip "org:" prefix if present
  const rawRole = membership?.role || membership?.roleName || null;
  const userRole = rawRole
    ? rawRole.toLowerCase().replace('org:', '')
    : ROLES.STUDENT;

  /**
   * Check if user has a specific role
   * @param {string} role - Role to check against
   * @returns {boolean}
   */
  const hasRole = (role) => {
    if (!userRole) return false;
    return userRole === role.toLowerCase();
  };

  /**
   * Check if user has any of the specified roles
   * @param {string[]} roles - Array of roles to check
   * @returns {boolean}
   */
  const hasAnyRole = (roles) => {
    if (!userRole || !Array.isArray(roles)) return false;
    return roles.some((role) => userRole === role.toLowerCase());
  };

  /**
   * Check if user has equal or higher role in hierarchy
   * @param {string} requiredRole - Minimum required role
   * @returns {boolean}
   */
  const hasMinimumRole = (requiredRole) => {
    if (!userRole) return false;
    const userLevel = ROLE_HIERARCHY[userRole] || 0;
    const requiredLevel = ROLE_HIERARCHY[requiredRole.toLowerCase()] || 0;
    return userLevel >= requiredLevel;
  };

  /**
   * Check if user can access a route based on allowed roles
   * @param {string[]} allowedRoles - Array of roles allowed to access the route
   * @returns {boolean}
   */
  const canAccessRoute = (allowedRoles) => {
    return hasAnyRole(allowedRoles);
  };

  // Convenience role checkers
  const isAdmin = hasRole(ROLES.ADMIN);
  const isTeacher = hasRole(ROLES.TEACHER);
  const isStudent = hasRole(ROLES.STUDENT);

  // Permission checkers (hierarchical)
  const canAccessAdmin = isAdmin;
  const canAccessTeacher = isAdmin || isTeacher;
  const canAccessStudent = isAdmin || isTeacher || isStudent;

  return {
    // Role state
    userRole,
    isAuthenticated: !!userRole,

    // Role checkers
    isAdmin,
    isTeacher,
    isStudent,

    // Permission checkers
    canAccessAdmin,
    canAccessTeacher,
    canAccessStudent,

    // Utility functions
    hasRole,
    hasAnyRole,
    hasMinimumRole,
    canAccessRoute,
  };
};
